/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EmployeeList.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EmployeeList - Список сотрудников проекта с направляющим пустым состоянием.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: заголовок списка, счётчик ролей, направляющее пустое состояние.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EmployeeItem from './EmployeeItem';

/* START_METHOD_EmployeeList */
/* START_CONTRACT: EmployeeList
 * PURPOSE: Отобразить список сотрудников или заглушку при пустом списке.
 * INPUTS: { employees: Employee[], onEdit: (e)=>void, onDelete: (e)=>void }.
 * OUTPUTS: { JSX.Element - список }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: EmployeeList
 */
export default function EmployeeList({ employees, onEdit, onDelete }) {
  if (employees.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
          backgroundImage: (theme) =>
            `radial-gradient(ellipse 80% 60% at 50% 0%, ${theme.palette.primary.main}08 0%, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <PeopleOutlineIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Команда пока пуста
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
          Добавьте сотрудников и назначьте им проектные роли — так вы сможете планировать загрузку и отсутствия.
        </Typography>
      </Paper>
    );
  }

  const roleCount = new Set(employees.map((e) => e.projectRoleId)).size;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            letterSpacing: '0.1em',
            lineHeight: 1.4,
          }}
        >
          Состав
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {employees.length} чел.
          {roleCount > 0 && ` · ${roleCount} ${roleCount === 1 ? 'роль' : roleCount < 5 ? 'роли' : 'ролей'}`}
        </Typography>
      </Box>

      {employees.map((employee, index) => (
        <EmployeeItem
          key={employee.id}
          employee={employee}
          onEdit={onEdit}
          onDelete={onDelete}
          isLast={index === employees.length - 1}
        />
      ))}
    </Paper>
  );
}
/* END_METHOD_EmployeeList */
